import { useState, useEffect } from 'react';
import { config } from '../config';

export interface PaginationOptions {
  /**
   * Default number of items to show per page
   */
  defaultItemsPerPage?: number;
  /**
   * Available options for items per page dropdown
   */
  itemsPerPageOptions?: number[];
  /**
   * Total number of items to paginate
   */
  totalItems: number;
}

/**
 * Custom hook for handling pagination logic
 * Can be used with any component that needs pagination
 * 
 * Usage pattern:
 * 1. Import the hook and the Pagination component
 * ```
 * import { usePagination } from '../../hooks/usePagination';
 * import Pagination from '../common/Pagination';
 * import { config } from '../../config';
 * ```
 * 
 * 2. Use the hook in your component, with the appropriate config value
 * ```
 * const { 
 *   currentPage, 
 *   itemsPerPage, 
 *   totalPages, 
 *   itemsPerPageOptions,
 *   paginate, 
 *   handleItemsPerPageChange,
 *   getCurrentItems
 * } = usePagination({
 *   totalItems: items.length,
 *   defaultItemsPerPage: config.pagination.defaultItemsPerPage
 * });
 * ```
 * 
 * 3. Get the current page items
 * ```
 * const { currentItems: paginatedItems } = getCurrentItems(items);
 * ```
 * 
 * 4. Render the Pagination component
 * ```
 * <Pagination 
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   paginate={paginate}
 *   itemsPerPage={itemsPerPage}
 *   totalItems={items.length}
 *   itemsPerPageOptions={itemsPerPageOptions}
 *   handleItemsPerPageChange={handleItemsPerPageChange}
 *   ariaLabel={t('pagination.itemsPagination', 'Items pagination')}
 * />
 * ```
 */
export function usePagination({
  defaultItemsPerPage = config.pagination.defaultItemsPerPage,
  itemsPerPageOptions = config.pagination.itemsPerPageOptions,
  totalItems
}: PaginationOptions) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to first page when data changes or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems, itemsPerPage]);

  // Handle page change
  const paginate = (pageNumber: number) => {
    setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get current items
  const getCurrentItems = <T>(items: T[]) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return {
      currentItems: items.slice(indexOfFirstItem, indexOfLastItem),
      indexOfFirstItem,
      indexOfLastItem: Math.min(indexOfLastItem, items.length)
    };
  };

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    itemsPerPageOptions,
    setCurrentPage,
    setItemsPerPage,
    paginate,
    handleItemsPerPageChange,
    getCurrentItems,
    // Information about the current page
    paginationInfo: {
      startIndex: (currentPage - 1) * itemsPerPage,
      endIndex: Math.min(currentPage * itemsPerPage, totalItems),
      totalItems
    }
  };
} 